import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { CarFilter, SpecCountType } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const fuelTypesState = atom<CarFilter[]>({
  key: 'fuelTypes',
  default: [],
});

export const fuelTypesCountState = atom<SpecCountType[]>({
  key: 'fuelTypesCount',
  default: [],
});

export const fuelTypeSearchOptionsSelector = selector({
  key: 'fuelTypeSearchOptionsSelector',
  get: ({ get }) => {
    const fuelTypes = get(fuelTypesState);
    const fuelTypeSearchOptions: CarFilter[] = fuelTypes.filter(
      (option) => !option.isSelected,
    );

    return { fuelTypeSearchOptions };
  },
});

export const toggleFuelTypeSelector = selector<number | null>({
  key: 'toggleFuelType',
  get: () => null,
  set: ({ set, get }, fuelTypeId) => {
    const fuelTypes = get(fuelTypesState);

    const updatedFuelType = produce(fuelTypes, (fuelTypesDraft) => {
      if (fuelTypeId) {
        const fuelType = fuelTypesDraft.find(
          (fuelType) => fuelType.id === fuelTypeId,
        );

        fuelType!.isSelected = !fuelType?.isSelected;
        return;
      }
    });

    set(fuelTypesState, updatedFuelType);
  },
});

export const hasSelectedFuelTypesFilterSelector = selector<boolean>({
  key: 'hasSelectedFuelTypesFilter',
  get({ get }) {
    return Boolean(get(selectedFuelTypesSelector).length);
  },
});

export const visibleFuelTypeSelector = selector({
  key: 'visibleFuelTypeSelector',
  get: ({ get }) => {
    const fuelTypes = get(fuelTypesState);
    const fuelTypeCount = get(fuelTypesCountState);

    return fuelTypes.map((fuelType) => ({
      ...fuelType,
      isDisabled: !fuelTypeCount?.find(
        (group) => group?.specId === fuelType?.id,
      )?.count,
    }));
  },
});

export const selectedFuelTypesSelector = selector<CarFilter[]>({
  key: 'selectedFuelTypes',
  get: ({ get }) => {
    const fuelTypes = get(fuelTypesState);

    return fuelTypes.filter((fuelType) => fuelType.isSelected);
  },
});

export const resetSelectedFuelTypesSelector = selector<number | null>({
  key: 'resetSelectedFuelTypes',
  get: () => null,
  set: ({ set, get }, fuelTypeId) => {
    const fuelTypes = get(fuelTypesState);

    const updatedFuelType = produce(fuelTypes, (fuelTypesDraft) => {
      if (fuelTypeId) {
        const fuelType = fuelTypesDraft.find(
          (fuelType) => fuelType.id === fuelTypeId,
        );

        fuelType!.isSelected = false;
        return;
      }

      fuelTypesDraft.forEach((fuelType) => {
        if (fuelTypeId && fuelType.id === fuelTypeId) {
          fuelType.isSelected = false;
          return;
        }
        fuelType.isSelected = false;
      });
    });

    set(fuelTypesState, updatedFuelType);
  },
});

export const selectFuelTypeSelector = selector({
  key: 'selectFuelType',
  get: () => null as any,
  set: ({ set, get }, fuelTypeIds) => {
    const fuelTypes = get(fuelTypesState);

    const updatedFuelType = produce(fuelTypes, (fuelTypesDraft) => {
      if (guardRecoilDefaultValue(fuelTypeIds)) return;
      if (fuelTypeIds.length) {
        fuelTypeIds
          .map((fuelTypeId: any) => Number(fuelTypeId))
          .forEach((fuelTypeId: any) => {
            const fuelType = fuelTypesDraft.find(
              (fuelType) => fuelType.id === fuelTypeId,
            );

            if (!fuelType) return;
            fuelType.isSelected = true;
          });

        return;
      }
    });

    set(fuelTypesState, updatedFuelType);
  },
});
